import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { InvoiceStatBand } from './components/invoice-stat-band';
import { InvoiceListSection } from './components/invoice-list-section';
import { invoiceColumns } from './list-configs/invoice-column-definitions';
import { buildInvoiceFilters } from './list-configs/invoice-filter-fields';
import { useProductGroupOptions } from '@/api/product-groups';

/**
 * Pre-filtered list tabs mirroring the legacy invoice module. Each tab is the
 * same server-paginated list scoped by a fixed baseline `filters` preset (the
 * backend decodes these from the base64 `filters` param — verified live). The
 * Waiting-Confirm bulk-approve workflow (a mutation) is intentionally not wired
 * here; this tab is read-only for now.
 */
const INVOICE_TABS: { value: string; label: string; preset?: Record<string, unknown> }[] = [
  { value: 'all', label: 'All Invoices' },
  {
    value: 'waiting-confirm',
    label: 'Waiting Confirm',
    preset: { invoice_status: ['waiting-confirm'], revenue_confirmed: [false] },
  },
  {
    value: 'invoicable',
    label: 'Invoicable',
    preset: { invoicable: [true], revenue_confirmed: [true], invoice_status: ['approved', 'paid', 'done'] },
  },
  {
    value: 'non-invoicable',
    label: 'Non-Invoicable',
    preset: { invoicable: [false], revenue_confirmed: [true], invoice_status: ['approved', 'paid', 'done'] },
  },
  { value: 'deposit', label: 'Deposit', preset: { invoice_type: ['deposit'] } },
];

const TAB_VALUES = INVOICE_TABS.map((t) => t.value);

/**
 * Invoices — the ported DataCentral v4 global invoice manager. A `PageHeader`
 * over a per-status stat band, then pre-filtered tabs (All / Waiting Confirm /
 * Invoicable / Non-Invoicable / Deposit), each rendering the server-paginated
 * `InvoiceListSection` (search + filters + column visibility + row-select merge).
 */
export default function InvoicesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: productGroupOptions } = useProductGroupOptions();
  const filterFields = useMemo(
    () => buildInvoiceFilters(productGroupOptions ?? []),
    [productGroupOptions],
  );

  const requested = searchParams.get('tab');
  const activeTab = requested && TAB_VALUES.includes(requested) ? requested : 'all';
  const onTabChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <Container size="full">
      <PageHeader
        title="Invoices"
        description="Billing invoices across their lifecycle — waiting confirm, approved, paid and settled."
        style={{ marginBottom: 'var(--vn-space-4)' }}
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <InvoiceStatBand />

        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList>
            {INVOICE_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {INVOICE_TABS.map((t) => (
            <TabsContent key={t.value} value={t.value}>
              {activeTab === t.value ? (
                <InvoiceListSection
                  columns={invoiceColumns}
                  filterFields={filterFields}
                  presetFilters={t.preset}
                  onRowClick={(r) => navigate(`/invoices/${r.invoice_id}`)}
                  searchPlaceholder="Search invoices…"
                />
              ) : null}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Container>
  );
}
